import { COMMON_FONT_SIZES } from "./CommonConstants";

/**
 * Common Utils
 */
class CommonUtils {
  /**
   * Returns common border styles based on the provided border radius.
   *
   * The border styles are common for the following components
   *
   * Form :
   *  Button
   *  Input
   *  Select
   * Data Display:
   *  Card
   *
   * @param {string} borderRadius .
   * @returns {Object} An object containing the border styles: { borderStyle, borderRadius, borderWidth }.
   */
  static getCommonBorderStyles(borderRadius) {
    // Border Radius
    const commonBorderRadiusLookup = {
      none: "0px",
      xs: "5px",
      sm: "10px",
      md: "15px",
      lg: "20px",
    };

    const DEFAULT_BORDER_RADIUS = "md";
    const BORDER_RADIUS = CommonUtils.getCSSPropertyValue(
      borderRadius,
      commonBorderRadiusLookup,
      true,
      DEFAULT_BORDER_RADIUS
    );

    return {
      borderStyle: "solid",
      borderRadius: BORDER_RADIUS,
      borderWidth: "1px",
    };
  }

  /**
   * Returns common font styles based on the provided font radius.
   *
   * The font styles are common for the following components
   *
   * @param {string} fontSize
   * @returns {Object} An object containing the font styles: { fontSize }.
   */
  static getCommonFontStyles(fontSize, textTransform) {
    // Font Size
    const commonFontSizeLookup = {
      xs: COMMON_FONT_SIZES.xs,
      sm: COMMON_FONT_SIZES.sm,
      md: COMMON_FONT_SIZES.md,
      lg: COMMON_FONT_SIZES.lg,
      xl: COMMON_FONT_SIZES.xl,
    };

    const DEFAULT_FONT_SIZE = "md";
    const FONT_SIZE = CommonUtils.getCSSPropertyValue(
      fontSize,
      commonFontSizeLookup,
      true,
      DEFAULT_FONT_SIZE
    );

    // Text Transform
    const commonTextTransformLookup = {
      none: "none",
      capitalize: "capitalize",
      lowercase: "lowercase",
      uppercase: "uppercase",
    };

    const DEFAULT_TEXT_TRANSFORM = "none";
    const TEXT_TRANSFORM = CommonUtils.getCSSPropertyValue(
      textTransform,
      commonTextTransformLookup,
      false,
      DEFAULT_TEXT_TRANSFORM
    );

    return {
      fontSize: FONT_SIZE,
      textTransform: TEXT_TRANSFORM,
    };
  }

  /**
   * Get the CSS property value from the provided propertyLookup based on the given property.
   * If the property is not found in the propertyLookup, and it matches a valid pixel value (e.g., '10px'),
   * it returns the same property. Otherwise, it falls back to the defaultProperty.
   *
   * @param {string} property - The property to look up in the propertyLookup.
   * @param {Object} propertyLookup - An object containing property-value pairs for the lookup.
   * @param {boolean} acceptCustomValueInPX - A boolean indicating whether custom property values in pixels are accepted.
   * @param {string} defaultProperty - The default property to use when the property is not found in the propertyLookup.
   * @returns {string} The CSS property value from propertyLookup or defaultProperty if not found.
   */
  static getCSSPropertyValue(
    property,
    propertyLookup,
    acceptCustomPropertyInPX,
    defaultProperty
  ) {
    if (property in propertyLookup) {
      return propertyLookup[property];
    } else if (
      new RegExp(/^\d+px$/).test(property) &&
      acceptCustomPropertyInPX
    ) {
      return property;
    } else {
      return propertyLookup[defaultProperty];
    }
  }
}

export default CommonUtils;
